// sRGB and OKLCH, and the WCAG contrast between them. Pure, so the theme rules can be tested for every host color.

export type Rgb = readonly [number, number, number];

export interface Oklch {
  l: number;
  c: number;
  h: number;
}

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const toLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
const toGamma = (v: number) => (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);

export function rgbToOklch([r, g, b]: Rgb): Oklch {
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);
  const L = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const M = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const S = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  const l = 0.2104542553 * L + 0.793617785 * M - 0.0040720468 * S;
  const a = 1.9779984951 * L - 2.428592205 * M + 0.4505937099 * S;
  const bb = 0.0259040371 * L + 0.7827717662 * M - 0.808675766 * S;
  const c = Math.hypot(a, bb);
  const h = c < 1e-4 ? 0 : ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360;
  return { l, c, h };
}

function oklchToLinear({ l, c, h }: Oklch): Rgb {
  const rad = (h * Math.PI) / 180;
  const a = c * Math.cos(rad);
  const b = c * Math.sin(rad);
  const L = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const M = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const S = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S,
    -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S,
    -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S,
  ];
}

const inGamut = (rgb: Rgb) => rgb.every((v) => v >= -0.0005 && v <= 1.0005);

/** Keeps lightness and hue and lowers chroma until the color exists in sRGB, which is what browsers show. */
export function fitGamut(color: Oklch): Oklch {
  if (inGamut(oklchToLinear(color))) return color;
  let low = 0;
  let high = color.c;
  for (let i = 0; i < 16; i += 1) {
    const mid = (low + high) / 2;
    if (inGamut(oklchToLinear({ ...color, c: mid }))) low = mid;
    else high = mid;
  }
  return { ...color, c: low };
}

export function oklchToRgb(color: Oklch): Rgb {
  const [r, g, b] = oklchToLinear(fitGamut(color));
  return [clamp01(toGamma(clamp01(r))), clamp01(toGamma(clamp01(g))), clamp01(toGamma(clamp01(b)))];
}

export function toCss(color: Oklch, alpha?: number): string {
  const fitted = fitGamut(color);
  const body = `${fitted.l.toFixed(4)} ${fitted.c.toFixed(4)} ${fitted.h.toFixed(2)}`;
  return alpha === undefined ? `oklch(${body})` : `oklch(${body} / ${alpha})`;
}

function relativeLuminance([r, g, b]: Rgb): number {
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

export function contrast(a: Oklch, b: Oklch): number {
  const la = relativeLuminance(oklchToRgb(a));
  const lb = relativeLuminance(oklchToRgb(b));
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

const NUMBER = "[-+]?(?:\\d+\\.?\\d*|\\.\\d+)";

function hslToRgb(h: number, s: number, l: number): Rgb {
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return l - s * Math.min(l, 1 - l) * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return [f(0), f(8), f(4)];
}

/** Accepts what hosts actually send: hex, rgb(), hsl(), oklch(). Alpha is ignored: a theme color must be opaque to be judged. */
export function parseColor(input: string): Oklch | null {
  const value = input.trim().toLowerCase();

  const hex = /^#([0-9a-f]{3,8})$/.exec(value)?.[1];
  if (hex && [3, 4, 6, 8].includes(hex.length)) {
    const full = hex.length <= 4 ? [...hex].map((ch) => ch + ch).join("") : hex;
    return rgbToOklch([parseInt(full.slice(0, 2), 16) / 255, parseInt(full.slice(2, 4), 16) / 255, parseInt(full.slice(4, 6), 16) / 255]);
  }

  const fn = new RegExp(`^(rgba?|hsla?|oklch)\\(\\s*(${NUMBER})(%|deg)?[\\s,]+(${NUMBER})(%)?[\\s,]+(${NUMBER})(%|deg)?(?:\\s*[,/]\\s*${NUMBER}%?)?\\s*\\)$`).exec(value);
  if (!fn) return null;
  const [, name, aRaw, aUnit, bRaw, bUnit, cRaw] = fn;
  const a = Number(aRaw);
  const b = Number(bRaw);
  const c = Number(cRaw);
  if (name!.startsWith("rgb")) {
    if ([a, b, c].some((v) => v < 0 || v > 255)) return null;
    return rgbToOklch([a / 255, b / 255, c / 255]);
  }
  if (name!.startsWith("hsl")) {
    if (b < 0 || b > 100 || c < 0 || c > 100) return null;
    return rgbToOklch(hslToRgb(((a % 360) + 360) % 360, b / 100, c / 100));
  }
  const l = aUnit === "%" ? a / 100 : a;
  const chroma = bUnit === "%" ? (b / 100) * 0.4 : b;
  if (l < 0 || l > 1 || chroma < 0) return null;
  return fitGamut({ l, c: chroma, h: ((c % 360) + 360) % 360 });
}
